// Posts: the blog's and the resources'. A post that is not there is `null`,
// not an error, the way a chapter nobody has written on is.
import type { PostInput, PostSection, PostSummary, PostView, UploadedImage } from "@bibleunleashed/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, del, post, put } from "./api";

const key = (slug: string) => ["post", slug];
const INDEX_KEY = ["posts"];

/** How each section names itself, and what it calls one of its own. */
export const SECTIONS: Record<PostSection, { name: string; noun: string }> = {
  blog: { name: "Blog", noun: "post" },
  resources: { name: "Resources", noun: "resource" },
};

/** "4 Sep 2026", as the blog prints a date. */
export const postDate = (iso: string): string =>
  new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

/** Every tag on a published post, commonest first. */
export function tagCounts(posts: PostSummary[]): [string, number][] {
  const counts = new Map<string, number>();
  for (const p of posts) {
    if (p.status === "published") for (const t of p.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
}

export function usePosts(section: PostSection) {
  return useQuery({
    queryKey: [...INDEX_KEY, section],
    queryFn: () => api<PostSummary[]>(`/api/posts?section=${section}`),
  });
}

export function usePost(slug: string | undefined) {
  return useQuery({
    queryKey: key(slug ?? ""),
    enabled: Boolean(slug),
    queryFn: async () => {
      try {
        return await api<PostView>(`/api/posts/${encodeURIComponent(slug!)}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * Creates the post when `id` is null and saves over it otherwise. `from` is
 * the address it was saved from: a save can move a post, and the old address
 * must not go on answering with it.
 */
export function useSavePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, doc }: { id: string | null; doc: PostInput; from?: string }) =>
      id ? put<PostView>(`/api/posts/${id}`, doc) : post<PostView>("/api/posts", doc),
    onSuccess: (saved, { from }) => {
      if (from && from !== saved.slug) qc.setQueryData(key(from), null);
      qc.setQueryData(key(saved.slug), saved);
      void qc.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}

export function useDeletePost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; slug: string }) => del<null>(`/api/posts/${id}`),
    onSuccess: (_done, { slug }) => {
      qc.setQueryData(key(slug), null);
      void qc.invalidateQueries({ queryKey: INDEX_KEY });
    },
  });
}

/** An image uploaded for a post's or a chapter's link preview, named by id when that is saved. */
export function useUploadImage() {
  return useMutation({
    mutationFn: (image: Blob) =>
      api<UploadedImage>("/api/images", { method: "POST", body: image, headers: { "Content-Type": image.type } }),
  });
}
